import { z } from 'zod';
import { env } from '../config/env';
import { notifyDataUnavailable } from '../lib/notifications';
import { ApiError, type ApiErrorCode } from './errors';

type QueryParams = Record<string, unknown>;

export interface RequestOptions<TBody = unknown, TSchema = unknown> {
  params?: QueryParams;
  body?: TBody;
  headers?: Record<string, string>;
  timeout?: number;
  correlationId?: string;
  signal?: AbortSignal;
  /** Keep small mutations alive while the browser is navigating away. */
  keepalive?: boolean;
  validate?: boolean;
  schema?: z.ZodType<TSchema>;
  /** Skip auth refresh attempt on 401 responses */
  skipAuthRefresh?: boolean;
}

export interface ApiResponseMetadata<TData> {
  data: TData;
  headers: Headers;
  status: number;
}

const DEFAULT_TIMEOUT_MS = 30_000;
const AUTH_REFRESH_PATH = '/api/v1/auth/refresh';
const AUTH_REFRESH_BLOCKLIST = new Set([
  AUTH_REFRESH_PATH,
  '/api/v1/auth/login',
  '/api/v1/auth/register',
  '/api/v1/auth/logout',
  '/api/v1/auth/bubble',
  '/api/v1/auth/callback/bubble',
]);

function buildSearchParams(params?: QueryParams) {
  const search = new URLSearchParams();

  if (!params) {
    return search;
  }

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;

    const appendValue = (val: unknown) => {
      if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') {
        search.append(key, String(val));
        return;
      }

      if (val instanceof Date) {
        search.append(key, val.toISOString());
        return;
      }

      search.append(key, JSON.stringify(val));
    };

    if (Array.isArray(value)) {
      value.forEach((entry) => {
        appendValue(entry);
      });
    } else {
      appendValue(value);
    }
  }

  return search;
}

function buildUrl(baseUrl: string, path: string, params?: QueryParams): string {
  const url = new URL(path, baseUrl);
  const search = buildSearchParams(params);

  const searchString = search.toString();
  if (searchString) {
    url.search = searchString;
  }

  return url.toString();
}

export class ApiClient {
  private readonly baseUrl: string;
  private readonly defaultTimeout: number;
  private refreshPromise: Promise<void> | null = null;

  constructor(baseUrl: string, defaultTimeout = DEFAULT_TIMEOUT_MS) {
    this.baseUrl = baseUrl;
    this.defaultTimeout = defaultTimeout;
  }

  private createController(signal?: AbortSignal) {
    const controller = new AbortController();

    if (signal) {
      if (signal.aborted) {
        controller.abort();
        return controller;
      }

      const abortListener = () => {
        controller.abort();
      };
      signal.addEventListener('abort', abortListener, { once: true });
    }

    return controller;
  }

  private async parseJson(response: Response, correlationId?: string): Promise<unknown> {
    try {
      return await response.json();
    } catch (error) {
      throw new ApiError({
        message: 'Failed to parse response body',
        status: response.status,
        code: 'PARSE_ERROR',
        correlationId,
        cause: error,
      });
    }
  }

  private async parseBlob(response: Response, correlationId?: string): Promise<Blob> {
    try {
      return await response.blob();
    } catch (error) {
      throw new ApiError({
        message: 'Failed to read response body',
        status: response.status,
        code: 'PARSE_ERROR',
        correlationId,
        cause: error,
      });
    }
  }

  private handleNonOk(response: Response, correlationId?: string) {
    return ApiError.fromResponse(response, correlationId);
  }

  private buildHeaders(
    correlationId: string,
    headers: Record<string, string> | undefined,
    body: BodyInit | undefined
  ) {
    const resolved: Record<string, string> = {
      Accept: 'application/json',
      'x-correlation-id': correlationId,
      'x-client-type': 'web',
      ...headers,
    };

    if (body !== undefined && !(body instanceof FormData)) {
      resolved['Content-Type'] ??= 'application/json';
    }

    return resolved;
  }

  private buildBody(body: unknown): BodyInit | undefined {
    if (body === undefined || body === null) return undefined;
    if (
      body instanceof FormData ||
      body instanceof Blob ||
      body instanceof ArrayBuffer ||
      ArrayBuffer.isView(body)
    ) {
      return body as BodyInit;
    }
    return JSON.stringify(body);
  }

  private createTimeout(timeout: number, controller: AbortController) {
    let timedOut = false;
    const id = setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, timeout);

    return {
      timedOut: () => timedOut,
      clear: () => {
        clearTimeout(id);
      },
    };
  }

  /** Reuse the application's coalesced account-session refresh from an owned Move surface. */
  refreshSession(): Promise<void> {
    return this.refreshAuthSession();
  }

  private async refreshAuthSession(): Promise<void> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.request('POST', AUTH_REFRESH_PATH, { skipAuthRefresh: true })
        .then(() => undefined)
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    await this.refreshPromise;
  }

  private wrapError(error: unknown, correlationId?: string, timedOut?: boolean) {
    if (timedOut) {
      return new ApiError({
        message: 'Request timed out',
        status: 0,
        code: 'REQUEST_TIMEOUT',
        correlationId,
        cause: error,
      });
    }

    if (error instanceof DOMException && error.name === 'AbortError') {
      return new ApiError({
        message: 'Request was aborted',
        status: 0,
        code: 'ABORTED',
        correlationId,
        cause: error,
      });
    }

    if (error instanceof ApiError) {
      return error;
    }

    return new ApiError({
      message: error instanceof Error ? error.message : 'Network error',
      status: 0,
      code: 'NETWORK_ERROR',
      correlationId,
      cause: error,
    });
  }

  private validateResponse<T>(
    data: unknown,
    schema?: z.ZodType<T>,
    validate = true,
    correlationId?: string
  ): T {
    if (!schema || !validate) {
      return data as T;
    }

    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      throw new ApiError({
        message: 'Response validation failed',
        status: 0,
        code: 'VALIDATION_ERROR',
        correlationId,
        details: parsed.error.issues,
      });
    }

    return parsed.data;
  }

  private async requestWithMetadata<TResponse, TBody = unknown>(
    method: string,
    path: string,
    options: RequestOptions<TBody, TResponse> = {},
    responseParser: (response: Response, correlationId?: string) => Promise<unknown> = (
      response,
      correlationId
    ) => this.parseJson(response, correlationId)
  ): Promise<ApiResponseMetadata<TResponse>> {
    const correlationId = options.correlationId ?? crypto.randomUUID();
    const controller = this.createController(options.signal);
    const timeout = this.createTimeout(options.timeout ?? this.defaultTimeout, controller);

    const url = buildUrl(this.baseUrl, path, options.params);

    try {
      const body = this.buildBody(options.body);
      const headers = this.buildHeaders(correlationId, options.headers, body);

      const response = await fetch(url, {
        method,
        headers,
        body,
        signal: controller.signal,
        keepalive: options.keepalive,
        // Include cookies for authentication (HttpOnly JWT cookies)
        credentials: 'include',
      });

      timeout.clear();

      if (!response.ok) {
        const error = await this.handleNonOk(response, correlationId);
        const shouldAttemptRefresh =
          response.status === 401 &&
          options.skipAuthRefresh !== true &&
          !AUTH_REFRESH_BLOCKLIST.has(path);

        if (error.code === 'DB_UNAVAILABLE') {
          notifyDataUnavailable();
        }

        if (shouldAttemptRefresh) {
          try {
            await this.refreshAuthSession();
            return await this.requestWithMetadata<TResponse, TBody>(
              method,
              path,
              { ...options, skipAuthRefresh: true },
              responseParser
            );
          } catch (refreshError) {
            if (refreshError instanceof ApiError && refreshError.status !== 401) {
              throw refreshError;
            }
            throw error;
          }
        }

        throw error;
      }

      const noContent = response.status === 204 || response.status === 205;
      if (noContent) {
        return {
          data: this.validateResponse<TResponse>(
            undefined,
            options.schema,
            options.validate,
            correlationId
          ),
          headers: response.headers,
          status: response.status,
        };
      }

      const data = await responseParser(response, correlationId);

      return {
        data: this.validateResponse<TResponse>(
          data,
          options.schema,
          options.validate,
          correlationId
        ),
        headers: response.headers,
        status: response.status,
      };
    } catch (error) {
      timeout.clear();
      throw this.wrapError(error, correlationId, timeout.timedOut());
    }
  }

  private async request<TResponse, TBody = unknown>(
    method: string,
    path: string,
    options: RequestOptions<TBody, TResponse> = {}
  ): Promise<TResponse> {
    const response = await this.requestWithMetadata(method, path, options);
    return response.data;
  }

  get<TResponse>(path: string, options?: RequestOptions<never, TResponse>) {
    return this.request<TResponse>('GET', path, options);
  }

  getWithMetadata<TResponse>(path: string, options?: RequestOptions<never, TResponse>) {
    return this.requestWithMetadata<TResponse>('GET', path, options);
  }

  getBlob(path: string, options?: RequestOptions<never, Blob>) {
    return this.requestWithMetadata<Blob, never>(
      'GET',
      path,
      { ...options, headers: { Accept: '*/*', ...options?.headers } },
      async (response, correlationId) => await this.parseBlob(response, correlationId)
    ).then((response) => response.data);
  }

  post<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: RequestOptions<TBody, TResponse>
  ) {
    return this.request<TResponse, TBody>('POST', path, { ...options, body });
  }

  postWithMetadata<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: RequestOptions<TBody, TResponse>
  ) {
    return this.requestWithMetadata<TResponse, TBody>('POST', path, { ...options, body });
  }

  put<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: RequestOptions<TBody, TResponse>
  ) {
    return this.request<TResponse, TBody>('PUT', path, { ...options, body });
  }

  putWithMetadata<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: RequestOptions<TBody, TResponse>
  ) {
    return this.requestWithMetadata<TResponse, TBody>('PUT', path, { ...options, body });
  }

  patch<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: RequestOptions<TBody, TResponse>
  ) {
    return this.request<TResponse, TBody>('PATCH', path, { ...options, body });
  }

  patchWithMetadata<TResponse, TBody = unknown>(
    path: string,
    body: TBody,
    options?: RequestOptions<TBody, TResponse>
  ) {
    return this.requestWithMetadata<TResponse, TBody>('PATCH', path, { ...options, body });
  }

  delete<TResponse, TBody = unknown>(path: string, options?: RequestOptions<TBody, TResponse>) {
    return this.request<TResponse, TBody>('DELETE', path, options);
  }
}

export const api = new ApiClient(env.VITE_API_URL);

export type { ApiErrorCode };
